import { ApiClientError, type ConsentType } from "@floline/api-client";

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

const CONSENT_ERROR_KEY_BY_TYPE: Partial<Record<ConsentType, string>> = {
  audio_transcription: "errors.consentRequiredAudioTranscription",
  ai_extraction: "errors.consentRequiredAiExtraction",
  ai_insights: "errors.consentRequiredAiInsights",
};

export function getConsentRequiredMessage(
  consentType: ConsentType | null,
  t: TranslateFn,
): string {
  const i18nKey =
    (consentType && CONSENT_ERROR_KEY_BY_TYPE[consentType]) ??
    "errors.consentRequired";
  return t(i18nKey);
}

function parseConsentType(details: unknown): ConsentType | null {
  if (!details || typeof details !== "object") {
    return null;
  }

  const maybeType = (details as Record<string, unknown>).consentType;
  if (typeof maybeType !== "string") {
    return null;
  }

  return maybeType as ConsentType;
}

export class ConsentRequiredClientError extends Error {
  readonly code = "CONSENT_REQUIRED";

  constructor(
    message: string,
    public readonly consentType: ConsentType | null,
  ) {
    super(message);
    this.name = "ConsentRequiredClientError";
  }
}

export function isConsentRequiredClientError(
  error: unknown,
): error is ConsentRequiredClientError {
  return error instanceof ConsentRequiredClientError;
}

export function mapConsentRequiredError(
  error: unknown,
  t: TranslateFn,
): ConsentRequiredClientError | null {
  if (!(error instanceof ApiClientError) || error.code !== "CONSENT_REQUIRED") {
    return null;
  }

  const consentType = parseConsentType(error.details);
  return new ConsentRequiredClientError(
    getConsentRequiredMessage(consentType, t),
    consentType,
  );
}
